import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './WordGame.css';
import settingsManager from '../utils/settingsManager.js';
import LetterGenerator from '../utils/LetterGenerator.js';
import debugLogger from '../utils/debugLogger.js';
import LetterSettings from './LetterSettings.js';
import LetterTile from './LetterTile.js';
import LetterPool from './LetterPool.js';
import LanguageToggle from './LanguageToggle.js';
import MessagePopup from './MessagePopup.js';
import useWordDictionary from '../hooks/useWordDictionary.js';
import useWordOptimizer from '../hooks/useWordOptimizer.js';
import { useAuth } from '../contexts/AuthContext.js';
import { useLanguage } from '../contexts/LanguageContext.js';
import { getLevel, getDifficultyTier } from '../config/levelData.js';
import firebaseDatabase from '../services/FirebaseDatabase.js';
import { analyzeLetterFeedback } from '../utils/letterFeedback.js';

/**
 * WordGame Component - Level-Based Puzzle Game
 *
 * A 100-level word puzzle game where players must spell specific target words
 * within 5 attempts per level. Sequential level unlocking with progressive difficulty.
 */

export default function WordGame({ onShowAuthModal, onShowUserProfile }) {
  // i18n and language context
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  const [letters, setLetters] = useState([]);
  const [spellingBar, setSpellingBar] = useState(Array(10).fill(null));
  const [spellingBarModifiers, setSpellingBarModifiers] = useState(Array(10).fill(null));
  const [draggedLetter, setDraggedLetter] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [draggedFromBar, setDraggedFromBar] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [dragOverSlot, setDragOverSlot] = useState(null);
  const [dragOverPool, setDragOverPool] = useState(false);
  const [draggedWord, setDraggedWord] = useState(null);
  const [isDraggingWord, setIsDraggingWord] = useState(false);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [isDraggingFromBar, setIsDraggingFromBar] = useState(false);
  const [isDraggingFromPool, setIsDraggingFromPool] = useState(false);
  const [currentScore, setCurrentScore] = useState(0);
  const [canSubmit, setCanSubmit] = useState(false);
  const [lettersBoughtThisRound, setLettersBoughtThisRound] = useState(0);

  // Touch drag state
  const [isTouchDragging, setIsTouchDragging] = useState(false);
  const [touchStartPosition, setTouchStartPosition] = useState({ x: 0, y: 0 });
  const [touchCurrentPosition, setTouchCurrentPosition] = useState({ x: 0, y: 0 });
  const [touchDragThreshold] = useState(10); // pixels to move before starting drag

  // Game state
  const [isGameOver, setIsGameOver] = useState(false);
  const [gameOverPromptShown, setGameOverPromptShown] = useState(false); // Prevent multiple download prompts

  // Refs for game state tracking
  const isMountedRef = useRef(true);

  // Guard for game over to prevent multiple executions
  const gameOverProcessingRef = useRef(false);

  // Word submission animation states
  const [showConfetti, setShowConfetti] = useState(false);
  const [exitingLetters, setExitingLetters] = useState([]);
  const [enteringLetters, setEnteringLetters] = useState([]);
  const [isAnimatingSubmission, setIsAnimatingSubmission] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // Loading state for word validation/submission

  // ========================================
  // LEVEL-BASED GAME STATE
  // ========================================

  // Current level (1-100)
  const [currentLevel, setCurrentLevel] = useState(1);

  // Attempts tracking (3 attempts per level)
  const [attemptsRemaining, setAttemptsRemaining] = useState(3);
  const [attemptsUsed, setAttemptsUsed] = useState([]); // Array of {word, score, isCorrect}

  // Level completion state
  const [levelCompleted, setLevelCompleted] = useState(false);
  const [levelFailed, setLevelFailed] = useState(false);
  // Capture the completed level for stable popup display
  const [completedLevelNumber, setCompletedLevelNumber] = useState(null);


  // Current level data
  const [levelData, setLevelData] = useState(null);
  const [targetWord, setTargetWord] = useState('');
  const [categoryHint, setCategoryHint] = useState('');

  // Solution display state
  const [showSolution, setShowSolution] = useState(false);
  const [solutionRevealed, setSolutionRevealed] = useState(false); // Track if solution was revealed in failed popup

  // Attempt history animation state
  const [flyingWord, setFlyingWord] = useState(null); // {word, score, isCorrect}


  // Wrong word popup state
  const [showWrongWordPopup, setShowWrongWordPopup] = useState(false);
  const [wrongWordMessage, setWrongWordMessage] = useState('');

  // ========================================
  // HINT SYSTEM STATE
  // ========================================
  const [hintsUsed, setHintsUsed] = useState(0); // Track hints used (max 3 per level)
  const [revealedHintLetters, setRevealedHintLetters] = useState([]); // Track which letters have been revealed

  // Initialize word dictionary for validation
  const {
    isLoaded: dictionaryLoaded,
    isLoading: dictionaryLoading,
    validateWord,
    error: dictionaryError
  } = useWordDictionary();

  // Add after existing hooks
  const {
    findOptimalPlacement,
    getQuickSuggestions,
    analyzeCurrentState,
    isAnalyzing: isOptimizing,
    lastAnalysis
  } = useWordOptimizer(validateWord);

  // Add state for word suggestions
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [currentSuggestions, setCurrentSuggestions] = useState(null);

  // Function to get word suggestions (legacy - not used in level-based gameplay)
  const handleGetSuggestions = useCallback(async () => {
    if (isGameOver) return;

    setShowSuggestions(true);
    const suggestions = await analyzeCurrentState(letters, spellingBar, spellingBarModifiers);
    setCurrentSuggestions(suggestions);
  }, [letters, spellingBar, spellingBarModifiers, analyzeCurrentState, isGameOver]);

  // ========================================
  // HINT SYSTEM FUNCTIONALITY
  // ========================================

  /**
   * Handle hint button click
   * Reveals a random unrevealed letter from the target word
   */
  const handleHint = useCallback(() => {
    if (hintsUsed >= 3) {
      console.log('❌ No hints remaining');
      return;
    }

    if (!targetWord) {
      console.log('❌ No target word set');
      return;
    }

    // Get unique letters from target word that haven't been revealed yet
    const targetLetters = targetWord.split('');
    const uniqueTargetLetters = [...new Set(targetLetters)];
    const unrevealedLetters = uniqueTargetLetters.filter(
      letter => !revealedHintLetters.includes(letter)
    );

    if (unrevealedLetters.length === 0) {
      console.log('❌ All unique letters in target word have been revealed');
      return;
    }

    // Select a random unrevealed letter
    const randomLetter = unrevealedLetters[Math.floor(Math.random() * unrevealedLetters.length)];

    // Find this letter in the letter pool
    const letterIndex = letters.findIndex(
      letter => letter && letter.char === randomLetter && !letter.isHintRevealed
    );

    if (letterIndex === -1) {
      console.log('❌ Letter not found in pool or already revealed');
      return;
    }

    // Mark this letter as hint-revealed
    setLetters(prevLetters => {
      const newLetters = [...prevLetters];
      if (newLetters[letterIndex]) {
        newLetters[letterIndex] = {
          ...newLetters[letterIndex],
          isHintRevealed: true
        };
      }
      return newLetters;
    });

    // Track this letter as revealed
    setRevealedHintLetters(prev => [...prev, randomLetter]);

    // Increment hints used
    setHintsUsed(prev => prev + 1);

    console.log(`💡 Hint revealed: "${randomLetter}" at index ${letterIndex} (${hintsUsed + 1}/3 hints used)`);
  }, [hintsUsed, targetWord, revealedHintLetters, letters]);

  /**
   * Check if hint button should be enabled
   */
  const canUseHint = useCallback(() => {
    if (hintsUsed >= 3) return false;
    if (!targetWord) return false;
    if (levelCompleted || levelFailed) return false;

    // Check if there are unrevealed letters
    const targetLetters = targetWord.split('');
    const uniqueTargetLetters = [...new Set(targetLetters)];
    const unrevealedLetters = uniqueTargetLetters.filter(
      letter => !revealedHintLetters.includes(letter)
    );

    return unrevealedLetters.length > 0;
  }, [hintsUsed, targetWord, revealedHintLetters, levelCompleted, levelFailed]);

  // Function to apply suggested placement (legacy - not used in level-based gameplay)
  const applySuggestion = useCallback((suggestion) => {
    if (!suggestion.placement) return;

    const newSpellingBar = Array(10).fill(null);
    const newLetters = [...letters];

    // Apply the suggested placement
    suggestion.placement.forEach((item, index) => {
      if (item) {
        newSpellingBar[index] = item.letter;
        // Mark letter as used in pool
        const letterIndex = newLetters.findIndex(letter =>
          letter && letter.char === item.letter.char && !letter.inBar
        );
        if (letterIndex !== -1) {
          newLetters[letterIndex] = null;
        }
      }
    });

    setSpellingBar(newSpellingBar);
    setLetters(newLetters);
    setShowSuggestions(false);
  }, [letters]);

  // Firebase authentication and database integration
  const {
    isAuthenticated,
    currentUser,
    playerData,
    // Session-based methods (new implementation)
    initializeGameSession,
    addSessionWord,
    finalizeGameSession,
    resetGameSession,
    getSessionStats
  } = useAuth();

  // Add this useEffect to set non-passive touch listeners
  useEffect(() => {
    // Get references to elements that need non-passive touch listeners
    const letterPool = document.querySelector('.letter-pool');
    const spellingBar = document.querySelector('.spelling-bar');

    // Handler for touchstart events - always prevent default to avoid scrolling during potential drags
    const handleTouchStartNonPassive = (e) => {
      // Only prevent default if the touch target is a draggable element
      const target = e.target.closest('.letter-tile-graphic, .spelling-slot');
      if (target) {
        e.preventDefault();
      }
    };

    // Handler for touchmove events - prevent default only when actively dragging
    const handleTouchMoveNonPassive = (e) => {
      if (isTouchDragging) {
        e.preventDefault();
      }
    };

    if (letterPool) {
      letterPool.addEventListener('touchstart', handleTouchStartNonPassive, { passive: false });
      letterPool.addEventListener('touchmove', handleTouchMoveNonPassive, { passive: false });
    }

    if (spellingBar) {
      spellingBar.addEventListener('touchstart', handleTouchStartNonPassive, { passive: false });
      spellingBar.addEventListener('touchmove', handleTouchMoveNonPassive, { passive: false });
    }

    // Clean up
    return () => {
      if (letterPool) {
        letterPool.removeEventListener('touchstart', handleTouchStartNonPassive);
        letterPool.removeEventListener('touchmove', handleTouchMoveNonPassive);
      }
      if (spellingBar) {
        spellingBar.removeEventListener('touchstart', handleTouchStartNonPassive);
        spellingBar.removeEventListener('touchmove', handleTouchMoveNonPassive);
      }
    };
  }, [isTouchDragging]);

  // Initialize settings and game session on component mount
  // NOTE: Letter generation is now handled by the level initialization useEffect
  useEffect(() => {
    console.log('🔄 WordGame initialization useEffect running at:', new Date().toISOString());

    // Clear debug logs at the start of each new game
    debugLogger.clearLogs();

    // Load letter settings (required for level letter generation)
    settingsManager.loadSettings();

    // Initialize game state
    setIsGameOver(false);

    // Initialize Firebase game session
    initializeGameSession().then(result => {
      if (!isMountedRef.current) return; // Component unmounted

      if (!result.success && result.error) {
        console.warn('Failed to load player data:', result.error);
        // Game continues with local data only
      } else {
        console.log('✅ Game session initialization completed successfully');
      }
    }).catch(error => {
      if (!isMountedRef.current) return; // Component unmounted
      console.error('Error initializing game session:', error);
      // Game continues with local data only
    });
  }, []); // Empty dependency array - only run once on mount

  // ========================================
  // HELPER FUNCTIONS
  // ========================================

  /**
   * Get the next uncompleted level for the player
   * Uses new three-state system: won, failed, not_done
   * Returns first level that is not 'won', starting from a specific level
   * @param {string} userId - User's UID
   * @param {string} language - Language code ('en' or 'da')
   * @param {number} startFrom - Level to start searching from (default: 1)
   * @returns {Promise<number>} Next uncompleted level (1-100)
   */
  const getNextUncompletedLevel = useCallback(async (userId, language, startFrom = 1) => {
    try {
      const playerData = await firebaseDatabase.getPlayerData(userId);

      if (!playerData.success) {
        return startFrom; // Default to startFrom if data fetch fails
      }

      // Use new levelStatus field (object mapping level numbers to status)
      const statusFieldName = `levelStatus_${language}`;
      const levelStatuses = playerData.data[statusFieldName] || {};

      // Find first level (startFrom-100) that is not 'won'
      // This includes both 'failed' and 'not_done' (null) levels
      // Use String() to ensure proper key access since Firebase stores keys as strings
      for (let level = startFrom; level <= 100; level++) {
        const status = levelStatuses[String(level)] || 'not_done';
        if (status !== 'won') {
          return level;
        }
      }

      // All levels from startFrom onwards are won! Return level 100
      return 100;
    } catch (error) {
      console.error('Error getting next uncompleted level:', error);
      return startFrom; // Fallback to startFrom
    }
  }, []);

  // ========================================
  // LEVEL INITIALIZATION
  // ========================================

  // Load player progress from Firebase on mount or language change
  useEffect(() => {
    const loadPlayerProgress = async () => {
      if (!currentUser) {
        console.log('📊 No user logged in, starting at level 1');
        setCurrentLevel(1);
        return;
      }

      try {
        console.log(`📊 Loading player progress for user ${currentUser.uid}, language: ${currentLanguage}...`);
        const result = await firebaseDatabase.getCurrentLevel(currentUser.uid, currentLanguage);

        console.log('📊 getCurrentLevel result:', result);

        if (result.success && result.currentLevel) {
          console.log(`✅ Loaded player progress: Level ${result.currentLevel} (${currentLanguage})`);
          console.log(`🔄 Setting currentLevel state to: ${result.currentLevel}`);
          setCurrentLevel(result.currentLevel);
        } else {
          console.log('📊 No saved progress found, starting at level 1');
          console.log('📊 Result details:', { success: result.success, currentLevel: result.currentLevel });
          setCurrentLevel(1);
        }
      } catch (error) {
        console.error('❌ Failed to load player progress:', error);
        setCurrentLevel(1); // Fallback to level 1 on error
      }
    };

    loadPlayerProgress();
  }, [currentUser, currentLanguage]); // Re-run when user or language changes

  // Load level data when currentLevel or language changes
  useEffect(() => {
    console.log(`🎯 Loading level ${currentLevel} data for language: ${currentLanguage}...`);

    const data = getLevel(currentLevel, currentLanguage);

    if (!data) {
      console.error(`❌ Invalid level number: ${currentLevel}`);
      return;
    }

    console.log(`✅ Level ${currentLevel} loaded:`, {
      targetWord: data.correctAnswer,
      category: data.category,
      wordLength: data.correctAnswer?.length,
      language: currentLanguage
    });

    // Set level data
    setLevelData(data);
    setTargetWord(data.correctAnswer);
    setCategoryHint(data.category);

    // Reset level state
    setAttemptsRemaining(3);
    setCompletedLevelNumber(null);

    setAttemptsUsed([]);
    setLevelCompleted(false);
    setLevelFailed(false);
    setShowSolution(false);
    setSolutionRevealed(false);
    setIsGameOver(false);

    // Reset hint system
    setHintsUsed(0);
    setRevealedHintLetters([]);

    // Use pre-generated letter pool from levelData (includes target + alternative words)
    // This ensures all alternative/distractor words can be spelled
    let levelLetters;

    if (data.letterPool && data.letterPool.length > 0) {
      // Convert the pre-generated letter pool to game format
      levelLetters = data.letterPool.map(char => {
        const letterInfo = settingsManager.getLetterInfo(char);
        return {
          char,
          points: letterInfo ? letterInfo.points : 1,
          inBar: false,
          isVowel: letterInfo ? letterInfo.isVowel : false,
          isRare: letterInfo ? letterInfo.isRare : false,
          isSuperRare: letterInfo ? letterInfo.super_rare : false
        };
      });

      // Shuffle the letters to randomize positions
      for (let i = levelLetters.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [levelLetters[i], levelLetters[j]] = [levelLetters[j], levelLetters[i]];
      }

      console.log(`🎲 Using pre-generated letter pool for level ${currentLevel}:`, {
        targetWord: data.correctAnswer,
        alternativeWords: data.alternativeWords,
        language: currentLanguage,
        totalLetters: levelLetters.length,
        letters: levelLetters.map(l => l.char).join(', ')
      });
    } else {
      // Fallback to dynamic generation if no pre-generated pool exists
      const difficultyTier = getDifficultyTier(currentLevel);
      levelLetters = LetterGenerator.generateLevelLetters(
        data.correctAnswer,
        settingsManager,
        difficultyTier.camouflageRatio,
        currentLanguage
      );

      console.log(`⚠️ No pre-generated pool found, dynamically generating for level ${currentLevel}:`, {
        targetWord: data.correctAnswer,
        language: currentLanguage,
        totalLetters: levelLetters.filter(l => l !== null).length,
        letters: levelLetters.filter(l => l !== null).map(l => l.char).join(', ')
      });
    }

    setLetters(levelLetters);

    // Clear spelling bar
    setSpellingBar(Array(10).fill(null));

    // Disable bonus fields for now (Phase 1)
    setSpellingBarModifiers(Array(10).fill(null));

  }, [currentLevel, currentLanguage]); // Re-run when level or language changes

  // Handle authentication state changes for session re-initialization
  // Update player progress immediately after login (unless we're in game-over processing)
  useEffect(() => {
    // Skip auth monitoring during game over to prevent session interference
    const isGameOverProcessing = gameOverPromptShown || isGameOver;

    console.log('🔐 Auth state change detected:', {
      isAuthenticated,
      isGameOverProcessing,
      isGameOver,
      gameOverPromptShown
    });

    if (isGameOverProcessing) {
      console.log('🔐 Auth change detected during gameplay/game over - IGNORING to preserve session data');
      console.log('🔄 Session will be re-initialized when you click "Start New Game"');
      return;
    }

    console.log('🔐 Authentication state changed, user:', isAuthenticated ? 'authenticated' : 'anonymous');

    // If the user just became authenticated, refresh their progress immediately
    if (isAuthenticated && currentUser) {
      console.log('🔄 Authenticated - refreshing player progress now');

      // Re-initialize session to load authenticated user data (scores, stats, etc.)
      initializeGameSession()
        .then((result) => {
          if (!isMountedRef.current) return;
          if (result?.success) {
            console.log('✅ Game session re-initialized after login');
          } else if (result && result.error) {
            console.warn('⚠️ Game session re-initialization after login reported an issue:', result.error);
          }
        })
        .catch((err) => {
          if (!isMountedRef.current) return;
          console.warn('⚠️ initializeGameSession() after login failed:', err);
        });

      // Fetch the correct current level for the authenticated user and update state
      (async () => {
        try {
          console.log(`📊 Loading player progress for user ${currentUser.uid}, language: ${currentLanguage}... (auth change)`);
          const result = await firebaseDatabase.getCurrentLevel(currentUser.uid, currentLanguage);
          console.log('📊 getCurrentLevel (auth change) result:', result);

          if (!isMountedRef.current) return;

          if (result?.success && result.currentLevel) {
            console.log(`🔄 Setting currentLevel to ${result.currentLevel} based on saved progress`);
            setCurrentLevel(result.currentLevel);
          } else {
            console.log('ℹ️ No saved progress found after login; keeping current level');
          }
        } catch (error) {
          if (!isMountedRef.current) return;
          console.error('❌ Failed to refresh player progress after login:', error);
        }
      })();
    } else {
      // Anonymous state or user signed out
      console.log('🔄 Auth state change noted - session will be re-initialized on next game start');
    }
  }, [isAuthenticated, currentUser, currentLanguage, isGameOver, gameOverPromptShown, initializeGameSession]); // Include game over state to prevent interference and refresh after login



  // Cleanup on unmount
  useEffect(() => {
    return () => {
      console.log('🧹 WordGame component unmounting');
      isMountedRef.current = false;
    };
  }, []);



  // Generate modifiers for spelling bar positions
  // PHASE 1: Bonus fields disabled for level-based gameplay
  function generateSpellingBarModifiers() {
    const modifiers = Array(10).fill(null);

    // DISABLED FOR PHASE 1: No bonus multipliers (DL, TL, DW, TW)
    // This simplifies scoring for level-based gameplay
    // May be re-enabled in Phase 2 with adjusted target scores

    setSpellingBarModifiers(modifiers);
  }

  // Letter generation functions moved to LetterGenerator utility

  // Handle buying a new letter
  function handleBuyLetter() {
    // Check if we can still buy letters (both pool space and round limit)
    if (!canBuyLetter()) return;

    const newLetters = [...letters];

    // First, try to find an empty slot in row 3 (positions 12-17)
    let emptyIndex = -1;
    for (let i = 12; i <= 17; i++) {
      if (newLetters[i] === null) {
        emptyIndex = i;
        break;
      }
    }

    // If row 3 is full, fall back to finding first empty slot anywhere
    if (emptyIndex === -1) {
      emptyIndex = newLetters.findIndex(letter => letter === null);
    }

    if (emptyIndex !== -1 && emptyIndex < 18) { // Ensure we don't exceed 18 positions
      // Generate single letter with simplified 50/50 vowel/consonant distribution
      const currentLettersForCheck = newLetters.filter(letter => letter !== null);
      const newLetter = LetterGenerator.generateSingleLetter(settingsManager, currentLettersForCheck);

      // Add the new letter to entering letters for bounce animation first
      const enteringLetterData = {
        letter: newLetter,
        index: emptyIndex,
        id: Math.random()
      };

      // Set entering letters first
      setEnteringLetters([enteringLetterData]);

      // Use setTimeout with 0 to ensure entering state is applied before letter appears
      setTimeout(() => {
        newLetters[emptyIndex] = newLetter;
        setLetters(newLetters);
      }, 0);

      // Clear the entrance animation after it completes
      setTimeout(() => {
        setEnteringLetters([]);
      }, 300); // Match the animation duration (0.3s)

      // Increment the bought letters counter
      setLettersBoughtThisRound(prev => prev + 1);
    }
  }

  // Check if buy letter button should be disabled (pool full or round limit reached)
  function canBuyLetter() {
    // Count non-null letters, ensuring we don't exceed 18 total positions
    const filledCount = letters.filter(letter => letter !== null).length;
    const poolHasSpace = filledCount < 18;
    const underRoundLimit = lettersBoughtThisRound < 3;

    return poolHasSpace && underRoundLimit;
  }

  // Get the reason why buy letter is disabled (for tooltip)
  function getBuyLetterDisabledReason() {
    const filledCount = letters.filter(letter => letter !== null).length;
    const poolFull = filledCount >= 18;
    const roundLimitReached = lettersBoughtThisRound >= 3;

    if (poolFull) {
      return 'Letter pool is full (18/18)';
    } else if (roundLimitReached) {
      return `Maximum 3 letters per round (${lettersBoughtThisRound}/3 bought)`;
    } else {
      return 'Buy a random letter';
    }
  }

  // Calculate current word score with multipliers
  function calculateWordScore() {
    let totalScore = 0;
    let wordMultiplier = 1;
    let hasLetters = false;

    for (let i = 0; i < spellingBar.length; i++) {
      const letter = spellingBar[i];
      if (letter) {
        hasLetters = true;
        let letterScore = letter.points;
        const modifier = spellingBarModifiers[i];

        // Apply letter multipliers first
        if (modifier === 'DL') {
          letterScore *= 2;
        } else if (modifier === 'TL') {
          letterScore *= 3;
        }

        totalScore += letterScore;

        // Accumulate word multipliers
        if (modifier === 'DW') {
          wordMultiplier *= 2;
        } else if (modifier === 'TW') {
          wordMultiplier *= 3;
        }
      }
    }

    // Apply word multipliers to total score
    const finalScore = hasLetters ? totalScore * wordMultiplier : 0;
    return finalScore;
  }

  // Check if word can be submitted (minimum 1 letter in spelling bar)
  function checkCanSubmit() {
    const filledSlots = spellingBar.filter(slot => slot !== null);
    return filledSlots.length >= 1;
  }

  // Update score and submission status when spelling bar changes
  useEffect(() => {
    const score = calculateWordScore();
    const canSubmitWord = checkCanSubmit() && !isGameOver;
    setCurrentScore(score);
    setCanSubmit(canSubmitWord);
  }, [spellingBar, spellingBarModifiers, isGameOver]);



  // Helper function to handle game over
  function handleGameOver() {
    console.log('🎮 GAME OVER - handleGameOver() called');
    console.log('🎮 Current game state:', {
      gameOverPromptShown,
      isGameOver,
      isAuthenticated
    });

    // CRITICAL FIX: Prevent multiple game over triggers with ref-based guard
    if (gameOverProcessingRef.current || gameOverPromptShown || isGameOver) {
      console.log('🎮 Game over already processed, skipping duplicate call');
      return;
    }

    // CRITICAL FIX: Mark game over immediately with both state and ref to prevent race conditions
    gameOverProcessingRef.current = true;
    setGameOverPromptShown(true);
    setIsGameOver(true);

    console.log('🎮 Starting game session finalization...');

    // CRITICAL FIX: Use setTimeout to ensure state updates are processed before finalization
    // This prevents timing issues with session stats calculation
    setTimeout(() => {
      // Get session stats after state updates are processed
      const sessionStats = getSessionStats();
      console.log('🎮 Session stats before finalization:', sessionStats);

      // Finalize game session with batch Firebase update
      finalizeGameSession().then(result => {
        console.log('🎮 finalizeGameSession() completed with result:', result);

        if (result.success) {
          if (result.isAnonymous) {
            console.log('👤 Anonymous game completed:', result.sessionStats);
            console.log('👤 Message:', result.message); // "Sign in to save your progress!"
          } else if (result.hasUpdates) {
            console.log('✅ Game progress saved to Firebase:', result.updateResults);
            if (result.updateResults.metersRecord.updated) {
              console.log(`🏆 New meters record: ${result.updateResults.metersRecord.newRecord} (previous: ${result.updateResults.metersRecord.previousRecord})`);
            }
            if (result.updateResults.topWords.updated) {
              console.log(`📝 ${result.updateResults.topWords.newWordsCount} new words made it to top words list`);
            }
          } else {
            console.log('📊 Game completed - no new records to save');
          }
        } else {
          console.error('❌ Failed to save game progress:', result.error);
          console.error('❌ Full error result:', result);
          // Game over continues even if save fails
        }
      }).catch(error => {
        console.error('❌ Error finalizing game session:', error);
        console.error('❌ Error stack:', error.stack);
        // Game over continues even if save fails
      });
    }, 100); // Small delay to ensure state updates are processed
  }

  // Helper function to reset game state
  function resetGameState() {
    console.log('🎮 resetGameState() called - Starting new game');
    console.log('🔐 Current auth state:', {
      isAuthenticated,
      hasPlayerData: !!playerData,
      playerDataPreview: playerData ? {
        metersRecord: playerData.metersSkatedRecord,
        totalScore: playerData.totalScore,
        bestGameScore: playerData.bestGameScore
      } : null
    });

    // Clear debug logs at the start of each new game
    debugLogger.clearLogs();

    setIsGameOver(false);
    setGameOverPromptShown(false); // Reset prompt guard for new game
    setLettersBoughtThisRound(0);

    // Reset game over processing ref for new game
    gameOverProcessingRef.current = false;

    // CRITICAL FIX: Re-initialize game session to load authenticated user data
    // This handles the case where user logged in at game over screen
    // Use setTimeout to ensure state updates are processed first
    setTimeout(() => {
      console.log('🔄 Re-initializing game session for new game (auth state:', isAuthenticated ? 'authenticated' : 'anonymous', ')');
      initializeGameSession().then(result => {
        if (result.success) {
          console.log('✅ Game session re-initialized successfully for new game');
          if (result.playerData) {
            console.log('✅ Loaded authenticated user data:', {
              totalScore: result.playerData.totalScore,
              bestGameScore: result.playerData.bestGameScore,
              topWordsCount: result.playerData.topWords?.length || 0
            });
          } else {
            console.log('ℹ️ Anonymous user - using default data');
          }
        } else if (result.error) {
          console.warn('⚠️ Failed to re-initialize game session:', result.error);
          // Game continues with current data
        }
      }).catch(error => {
        console.error('❌ Error re-initializing game session:', error);
        // Game continues with current data
      });

      // Reset current game stats (words, meters, points)
      resetGameSession();
    }, 100); // Small delay to ensure state updates are processed
  }

  // Helper function to find contiguous word starting from a position
  function findContiguousWord(startIndex) {
    if (!spellingBar[startIndex]) return null;

    // Find the start of the contiguous sequence
    let wordStart = startIndex;
    while (wordStart > 0 && spellingBar[wordStart - 1] !== null) {
      wordStart--;
    }

    // Find the end of the contiguous sequence
    let wordEnd = startIndex;
    while (wordEnd < spellingBar.length - 1 && spellingBar[wordEnd + 1] !== null) {
      wordEnd++;
    }

    // Check if this is the only contiguous sequence in the spelling bar
    let hasOtherLetters = false;
    for (let i = 0; i < spellingBar.length; i++) {
      if (spellingBar[i] !== null && (i < wordStart || i > wordEnd)) {
        hasOtherLetters = true;
        break;
      }
    }

    // Only return word info if this is the only sequence and we're dragging the first letter
    if (!hasOtherLetters && startIndex === wordStart) {
      const word = [];
      for (let i = wordStart; i <= wordEnd; i++) {
        word.push({
          letter: spellingBar[i],
          index: i
        });
      }
      return {
        letters: word,
        startIndex: wordStart,
        endIndex: wordEnd
      };
    }

    return null;
  }

  // Handle letter tap
  function handleLetterTap(index) {
    if (!letters[index] || letters[index].inBar) return;

    // Find the smart position to place the letter
    let targetPosition;

    // Check if spelling bar is completely empty
    const hasAnyLetters = spellingBar.some(pos => pos !== null);

    if (!hasAnyLetters) {
      // If no letters in spelling bar, place at position 0
      targetPosition = 0;
    } else {
      // Find the last occupied position
      let lastOccupiedIndex = -1;
      for (let i = spellingBar.length - 1; i >= 0; i--) {
        if (spellingBar[i] !== null) {
          lastOccupiedIndex = i;
          break;
        }
      }

      // Place at the first empty position after the last letter
      targetPosition = lastOccupiedIndex + 1;

      // If that position is beyond the bar length, find any empty position
      if (targetPosition >= spellingBar.length) {
        targetPosition = spellingBar.findIndex(pos => pos === null);
        if (targetPosition === -1) return; // Bar is full
      }
    }

    const newLetters = [...letters];
    const newSpellingBar = [...spellingBar];

    // Mark letter as used in pool (set to null) instead of removing it
    const letterToMove = newLetters[index];
    newLetters[index] = null;

    newSpellingBar[targetPosition] = {
      char: letterToMove.char,
      points: letterToMove.points,
      originalIndex: index, // Keep track of original pool position
      isVowel: letterToMove.isVowel,
      isRare: letterToMove.isRare,
      isSuperRare: letterToMove.isSuperRare,
      isHintRevealed: letterToMove.isHintRevealed || false
    };

    setLetters(newLetters);
    setSpellingBar(newSpellingBar);
  }

  // Drag start handlers
  function handleDragStart(e, index, fromBar = false) {
    // Set initial cursor position
    setCursorPosition({ x: e.clientX, y: e.clientY });

    if (fromBar) {
      // Check if this is the start of a word that can be dragged as a whole
      const word = findContiguousWord(index);
      if (word) {
        setDraggedWord(word);
        setIsDraggingWord(true);
        setDraggedLetter(spellingBar[index]);
      } else {
        setDraggedLetter(spellingBar[index]);
        setIsDraggingWord(false);
        setDraggedWord(null);
      }
      setDraggedFromBar(true);
      setIsDraggingFromBar(true);
    } else {
      setDraggedLetter({
        char: letters[index].char,
        points: letters[index].points,
        originalIndex: index,
        isVowel: letters[index].isVowel,
        isRare: letters[index].isRare,
        isSuperRare: letters[index].isSuperRare,
        isHintRevealed: letters[index].isHintRevealed || false
      });
      setDraggedFromBar(false);
      setIsDraggingWord(false);
      setDraggedWord(null);
      setIsDraggingFromBar(false);
      setIsDraggingFromPool(true);
    }
    setDraggedIndex(index);
  }

  // Handle drop on spelling bar slot
  function handleDrop(e, slotIndex) {
    e.preventDefault();

    if (!draggedLetter) return;

    const newSpellingBar = [...spellingBar];
    const newLetters = [...letters];

    // If dragging a whole word
    if (isDraggingWord && draggedWord) {
      // Clear the original word positions
      for (let i = draggedWord.startIndex; i <= draggedWord.endIndex; i++) {
        newSpellingBar[i] = null;
      }

      // Calculate the rightmost position where the word can fit
      const wordLength = draggedWord.letters.length;
      const maxStartPosition = Math.min(slotIndex, spellingBar.length - wordLength);

      // Place the word starting from the calculated position
      for (let i = 0; i < wordLength; i++) {
        const targetIndex = maxStartPosition + i;

        // If there's a letter in the target position, return it to first empty pool slot
        if (newSpellingBar[targetIndex]) {
          const displacedLetter = newSpellingBar[targetIndex];
          const emptyIndex = newLetters.findIndex(letter => letter === null);
          if (emptyIndex !== -1) {
            newLetters[emptyIndex] = {
              char: displacedLetter.char,
              points: displacedLetter.points,
              inBar: false,
              isVowel: displacedLetter.isVowel || false,
              isRare: displacedLetter.isRare || false,
              isSuperRare: displacedLetter.isSuperRare || false,
              isHintRevealed: displacedLetter.isHintRevealed || false
            };
          }
        }

        newSpellingBar[targetIndex] = draggedWord.letters[i].letter;
      }
    }
    // If dragged from letter rack
    else if (!draggedFromBar) {
      // Mark letter as used in pool (set to null) instead of removing it
      const letterToMove = newLetters[draggedLetter.originalIndex];
      newLetters[draggedLetter.originalIndex] = null;

      // If slot is occupied, return the displaced letter to first empty pool slot
      if (newSpellingBar[slotIndex]) {
        const displacedLetter = newSpellingBar[slotIndex];
        // Find first empty slot in pool
        const emptyIndex = newLetters.findIndex(letter => letter === null);
        if (emptyIndex !== -1) {
          newLetters[emptyIndex] = {
            char: displacedLetter.char,
            points: displacedLetter.points,
            inBar: false,
            isVowel: displacedLetter.isVowel || false,
            isRare: displacedLetter.isRare || false,
            isSuperRare: displacedLetter.isSuperRare || false,
            isHintRevealed: displacedLetter.isHintRevealed || false
          };
        }
      }

      // Place the new letter in the slot (preserve all properties including isHintRevealed)
      newSpellingBar[slotIndex] = {
        char: draggedLetter.char,
        points: draggedLetter.points,
        originalIndex: draggedLetter.originalIndex,
        isVowel: letterToMove.isVowel,
        isRare: letterToMove.isRare,
        isSuperRare: letterToMove.isSuperRare,
        isHintRevealed: letterToMove.isHintRevealed || false
      };
    }
    // If dragged from another slot in the spelling bar (single letter)
    else {
      // If dropping on the same position, do nothing
      if (draggedIndex === slotIndex) {
        resetDragState();
        return;
      }

      // If target slot is occupied, swap the letters
      if (newSpellingBar[slotIndex]) {
        const displacedLetter = newSpellingBar[slotIndex];

        // Place the displaced letter in the original position (preserve all properties)
        newSpellingBar[draggedIndex] = displacedLetter;

        // Place the dragged letter in the target position (preserve all properties)
        newSpellingBar[slotIndex] = draggedLetter;
      } else {
        // If target slot is empty, just move the letter
        newSpellingBar[draggedIndex] = null;
        newSpellingBar[slotIndex] = draggedLetter;
      }
    }

    setSpellingBar(newSpellingBar);
    setLetters(newLetters);
    resetDragState();
  }

  // Helper function to reset all drag states
  function resetDragState() {
    setDraggedLetter(null);
    setDraggedIndex(null);
    setDraggedFromBar(false);
    setDraggedWord(null);
    setIsDraggingWord(false);
    setDragOverSlot(null);
    setDragOverPool(false);
    setCursorPosition({ x: 0, y: 0 });
    setIsDraggingFromBar(false);
    setIsDraggingFromPool(false);
    // Reset touch drag state
    setIsTouchDragging(false);
    setTouchStartPosition({ x: 0, y: 0 });
    setTouchCurrentPosition({ x: 0, y: 0 });
  }

  // Track mouse movement during drag
  function handleMouseMove(e) {
    if (isDraggingWord || (isDraggingFromBar && !isDraggingWord)) {
      setCursorPosition({ x: e.clientX, y: e.clientY });
    }
  }

  // Handle drag events to track cursor position
  function handleDrag(e) {
    if ((isDraggingWord || (isDraggingFromBar && !isDraggingWord)) && e.clientX !== 0 && e.clientY !== 0) {
      setCursorPosition({ x: e.clientX, y: e.clientY });
    }
  }

  // Handle drop on letter pool (return letter to pool)
  function handleDropOnPool(e) {
    e.preventDefault();

    if (!draggedLetter || !draggedFromBar) return;

    const newSpellingBar = [...spellingBar];
    const newLetters = [...letters];

    // If dragging a whole word, return all letters to pool
    if (isDraggingWord && draggedWord) {
      for (const wordLetter of draggedWord.letters) {
        // Find first empty slot in pool
        const emptyIndex = newLetters.findIndex(letter => letter === null);
        if (emptyIndex !== -1) {
          newLetters[emptyIndex] = {
            char: wordLetter.letter.char,
            points: wordLetter.letter.points,
            inBar: false,
            isVowel: wordLetter.letter.isVowel || false,
            isRare: wordLetter.letter.isRare || false,
            isSuperRare: wordLetter.letter.isSuperRare || false,
            isHintRevealed: wordLetter.letter.isHintRevealed || false
          };
        }
        // Remove from spelling bar
        newSpellingBar[wordLetter.index] = null;
      }
    } else {
      // Single letter - find first empty slot in pool
      const emptyIndex = newLetters.findIndex(letter => letter === null);
      if (emptyIndex !== -1) {
        newLetters[emptyIndex] = {
          char: draggedLetter.char,
          points: draggedLetter.points,
          inBar: false,
          isVowel: draggedLetter.isVowel || false,
          isRare: draggedLetter.isRare || false,
          isSuperRare: draggedLetter.isSuperRare || false,
          isHintRevealed: draggedLetter.isHintRevealed || false
        };
      }

      // Remove from spelling bar
      newSpellingBar[draggedIndex] = null;
    }

    setSpellingBar(newSpellingBar);
    setLetters(newLetters);
    resetDragState();
  }

  // Global drop handler for dragging off the spelling bar
  function handleGlobalDrop(e) {
    // Only handle if we're dragging from the spelling bar and not dropping on a specific target
    if (draggedFromBar && draggedLetter) {
      // Check if the drop target is not within the spelling bar or letter pool
      const target = e.target;
      const spellingBar = target.closest('.spelling-bar');
      const letterPool = target.closest('.letter-pool');

      if (!spellingBar && !letterPool) {
        // Dropped outside both areas - return to pool
        handleDropOnPool(e);
      }
    }
  }

  // Handle drag over spelling bar slot with visual feedback
  function handleSlotDragOver(e, slotIndex) {
    e.preventDefault();
    setDragOverSlot(slotIndex);
  }

  // Handle drag leave spelling bar slot
  function handleSlotDragLeave(e) {
    e.preventDefault();
    setDragOverSlot(null);
  }

  // Handle drag over letter pool with visual feedback
  function handlePoolDragOver(e) {
    e.preventDefault();
    if (draggedFromBar) {
      setDragOverPool(true);
    }
  }

  // Handle drag leave letter pool
  function handlePoolDragLeave(e) {
    e.preventDefault();
    setDragOverPool(false);
  }

  // Reset drag states when drag ends
  function handleDragEnd() {
    // Small delay to allow drop events to fire first
    setTimeout(() => {
      resetDragState();
    }, 100);
  }

  // Touch event handlers for mobile drag and drop
  function handleTouchStart(e, index, fromBar = false) {
    const touch = e.touches[0];
    setTouchStartPosition({ x: touch.clientX, y: touch.clientY });
    setTouchCurrentPosition({ x: touch.clientX, y: touch.clientY });

    // Note: preventDefault is now handled by non-passive event listeners in useEffect
    // to avoid "Unable to preventDefault inside passive event listener" errors

    // Store the touch target info but don't start dragging yet
    // We'll start dragging only after the threshold is exceeded
    if (fromBar) {
      // Store info for potential word or single letter drag from spelling bar
      const word = findContiguousWord(index);
      if (word) {
        // Potential word drag
        setDraggedWord(word);
        setDraggedLetter(spellingBar[index]);
      } else {
        // Potential single letter drag
        setDraggedLetter(spellingBar[index]);
        setDraggedWord(null);
      }
      setDraggedFromBar(true);
      setDraggedIndex(index);
    } else {
      // Potential letter drag from pool
      if (letters[index]) {
        setDraggedLetter({
          char: letters[index].char,
          points: letters[index].points,
          originalIndex: index
        });
        setDraggedFromBar(false);
        setDraggedWord(null);
        setDraggedIndex(index);
      }
    }
  }

  function handleTouchMove(e) {
    if (!draggedLetter) return;

    const touch = e.touches[0];
    const currentPos = { x: touch.clientX, y: touch.clientY };
    setTouchCurrentPosition(currentPos);

    // Calculate distance moved from start
    const deltaX = currentPos.x - touchStartPosition.x;
    const deltaY = currentPos.y - touchStartPosition.y;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    // Start dragging if we've moved beyond threshold
    if (distance > touchDragThreshold && !isTouchDragging) {
      setIsTouchDragging(true);

      // Set up drag state similar to handleDragStart
      if (draggedFromBar) {
        if (draggedWord) {
          setIsDraggingWord(true);
        }
        setIsDraggingFromBar(true);
      } else {
        setIsDraggingFromPool(true);
      }

      // Update cursor position for floating elements
      setCursorPosition(currentPos);
    }

    // If we're actively dragging, update cursor position and prevent scrolling
    if (isTouchDragging) {
      setCursorPosition(currentPos);
      e.preventDefault();

      // Handle drag over detection for touch
      const elementBelow = document.elementFromPoint(currentPos.x, currentPos.y);
      if (elementBelow) {
        // Check if we're over a spelling slot
        const spellingSlot = elementBelow.closest('.spelling-slot');
        if (spellingSlot) {
          const slotIndex = Array.from(spellingSlot.parentNode.children).indexOf(spellingSlot);
          setDragOverSlot(slotIndex);
          setDragOverPool(false);
        }
        // Check if we're over the letter pool
        else if (elementBelow.closest('.letter-pool')) {
          setDragOverSlot(null);
          if (draggedFromBar) {
            setDragOverPool(true);
          }
        }
        // Clear drag over states if not over valid drop targets
        else {
          setDragOverSlot(null);
          setDragOverPool(false);
        }
      }
    }
  }

  function handleTouchEnd(e) {
    if (!draggedLetter) return;

    // If we were actively dragging, handle the drop
    if (isTouchDragging) {
      const touch = e.changedTouches[0];
      const dropPosition = { x: touch.clientX, y: touch.clientY };

      // Find the element at the drop position
      const elementBelow = document.elementFromPoint(dropPosition.x, dropPosition.y);

      if (elementBelow) {
        // Check if dropped on a spelling slot
        const spellingSlot = elementBelow.closest('.spelling-slot');
        if (spellingSlot) {
          const slotIndex = Array.from(spellingSlot.parentNode.children).indexOf(spellingSlot);
          // Simulate drop event
          const fakeEvent = { preventDefault: () => {} };
          handleDrop(fakeEvent, slotIndex);
          resetDragState();
          return;
        }

        // Check if dropped on letter pool (return to pool)
        if (elementBelow.closest('.letter-pool') && draggedFromBar) {
          const fakeEvent = { preventDefault: () => {} };
          handleDropOnPool(fakeEvent);
          resetDragState();
          return;
        }

        // Check if dropped outside valid areas (return to pool if from bar)
        if (draggedFromBar) {
          const fakeEvent = { preventDefault: () => {} };
          handleDropOnPool(fakeEvent);
          resetDragState();
          return;
        }
      }
    } else {
      // If we didn't drag (just a tap), handle as letter tap
      if (!draggedFromBar && draggedIndex !== null) {
        handleLetterTap(draggedIndex);
      }
    }

    // Reset all drag states
    resetDragState();
  }

  // Handle dropping letter back to rack
  function handleReturnToRack(barIndex) {
    const letterToReturn = spellingBar[barIndex];
    if (!letterToReturn) return;

    const newSpellingBar = [...spellingBar];
    const newLetters = [...letters];

    // Find first empty slot in pool
    const emptyIndex = newLetters.findIndex(letter => letter === null);
    if (emptyIndex !== -1) {
      newLetters[emptyIndex] = {
        char: letterToReturn.char,
        points: letterToReturn.points,
        inBar: false,
        isVowel: letterToReturn.isVowel || false,
        isRare: letterToReturn.isRare || false,
        isSuperRare: letterToReturn.isSuperRare || false,
        isHintRevealed: letterToReturn.isHintRevealed || false
      };
    }

    // Remove from spelling bar
    newSpellingBar[barIndex] = null;

    setSpellingBar(newSpellingBar);
    setLetters(newLetters);
  }

  const handleSettingsClose = () => {
    setShowSettings(false);
    // Regenerate letters in case settings changed
    // Initialize with 18 slots, first 15 filled, last 3 empty
    const newLetters = Array(18).fill(null);
    const randomLetters = LetterGenerator.generateRandomLetters(settingsManager);
    for (let i = 0; i < 15; i++) {
      newLetters[i] = randomLetters[i];
    }
    setLetters(newLetters);
    generateSpellingBarModifiers(); // Regenerate modifiers when settings change
    // Reset game state (this will also clear debug logs)
    resetGameState();
  };

  // Handle word submission - LEVEL-BASED GAMEPLAY (async for Cloud Function validation)
  async function handleWordSubmission() {
    // Check if level is already completed or failed
    if (levelCompleted) {
      alert('Level completed! Proceeding to next level...');
      return;
    }

    if (levelFailed) {
      alert('Level failed. You can retry this level.');
      return;
    }

    if (!canSubmit) {
      alert('You must place a letter in the center position (⭐) to submit a word!');
      return;
    }

    // Prevent multiple submissions during animation or validation
    if (isAnimatingSubmission || isSubmitting) {
      return;
    }

    // Get the current word from spelling bar
    const word = spellingBar
      .filter(letter => letter !== null)
      .map(letter => letter.char)
      .join('');

    if (word.length === 0) {
      alert('Please spell a word before submitting!');
      return;
    }

    // Set loading state at the start
    setIsSubmitting(true);

    try {
      // Check if dictionary is loaded
      if (!dictionaryLoaded) {
        if (dictionaryLoading) {
          // Show loading message in popup instead of alert
          const loadingMessage = 'Dictionary is still loading. Please wait a moment and try again.';
          setWrongWordMessage(loadingMessage);
          setShowWrongWordPopup(true);
          setIsSubmitting(false);
          return;
        } else if (dictionaryError) {
          // Dictionary error - show warning but continue with validation attempt
          console.warn(`Dictionary error: ${dictionaryError} - attempting validation anyway`);
        } else {
          // Show unavailable message in popup instead of alert
          const unavailableMessage = 'Dictionary not available. Please refresh the page.';
          setWrongWordMessage(unavailableMessage);
          setShowWrongWordPopup(true);
          setIsSubmitting(false);
          return;
        }
      } else {
        // Validate the word using the Cloud Function (async)
        try {
          const isValidWord = await validateWord(word);

          if (!isValidWord) {
            console.log('❌ Invalid word detected:', word);

            // Show invalid word popup instead of alert
            const invalidWordMessage = t('game.messages.invalidWord', { word });
            console.log('Setting wrong word message:', invalidWordMessage);
            setWrongWordMessage(invalidWordMessage);
            setShowWrongWordPopup(true);
            console.log('Popup state set to true');

            // Clear the spelling bar and return letters to pool
            const lettersToReturn = spellingBar.filter(letter => letter !== null);
            console.log('Returning letters to pool:', lettersToReturn.length);

            if (lettersToReturn.length > 0) {
              setLetters(prevLetters => {
                const newLetters = [...prevLetters];

                // Find empty slots in the letter pool and place returned letters
                lettersToReturn.forEach(letter => {
                  // Reset the letter's inBar property
                  const returnedLetter = { ...letter, inBar: false };

                  // Find first empty slot
                  const emptyIndex = newLetters.findIndex(l => l === null);
                  if (emptyIndex !== -1) {
                    newLetters[emptyIndex] = returnedLetter;
                  } else {
                    // If no empty slots, add to the end (shouldn't happen with 18 slots)
                    console.warn('No empty slot found in letter pool, adding to end');
                    newLetters.push(returnedLetter);
                  }
                });

                return newLetters;
              });
            }

            // Clear spelling bar
            setSpellingBar(Array(10).fill(null));
            console.log('Spelling bar cleared');

            // Reset submitting state
            setIsSubmitting(false);
            console.log('Submit button state reset');

            return;
          }
        } catch (error) {
          console.error('Word validation error:', error);

          // Show error popup instead of alert
          const errorMessage = 'Error validating word. Please try again.';
          setWrongWordMessage(errorMessage);
          setShowWrongWordPopup(true);

          // Clear the spelling bar and return letters to pool
          const lettersToReturn = spellingBar.filter(letter => letter !== null);
          if (lettersToReturn.length > 0) {
            setLetters(prevLetters => {
              const newLetters = [...prevLetters];

              // Find empty slots in the letter pool and place returned letters
              lettersToReturn.forEach(letter => {
                // Reset the letter's inBar property
                const returnedLetter = { ...letter, inBar: false };

                // Find first empty slot
                const emptyIndex = newLetters.findIndex(l => l === null);
                if (emptyIndex !== -1) {
                  newLetters[emptyIndex] = returnedLetter;
                } else {
                  // If no empty slots, add to the end (shouldn't happen with 18 slots)
                  console.warn('No empty slot found in letter pool, adding to end');
                  newLetters.push(returnedLetter);
                }
              });

              return newLetters;
            });
          }

          // Clear spelling bar
          setSpellingBar(Array(10).fill(null));

          // Reset submitting state
          setIsSubmitting(false);

          return;
        }
      }
    } finally {
      // Always clear loading state, even if validation fails
      // Note: We set it to false here for early returns, but will set to true again
      // during the animation phase below
    }

    // ========================================
    // LEVEL-BASED WIN/LOSS LOGIC
    // ========================================

    const isTargetWord = word.toUpperCase() === targetWord.toUpperCase();
    const isWinningAttempt = isTargetWord; // Win if word matches target word

    console.log('🎯 Level attempt:', {
      word,
      targetWord,
      score: currentScore,
      isTargetWord,
      isWinningAttempt,
      attemptsRemaining
    });

    // Record this attempt with letter feedback
    const attemptData = {
      word: word.toUpperCase(),
      score: currentScore,
      isCorrect: isWinningAttempt,
      timestamp: new Date().toISOString(),
      // Add letter-by-letter feedback for incorrect attempts (Wordle-style)
      letterFeedback: !isWinningAttempt ? analyzeLetterFeedback(word, targetWord) : null
    };

    // Clear submitting state now that validation is complete
    setIsSubmitting(false);

    // Trigger flying word animation
    setFlyingWord(attemptData);

    // Add to attempts history after animation starts
    setTimeout(() => {
      setAttemptsUsed(prev => [...prev, attemptData]);
      setFlyingWord(null); // Clear flying word after animation
    }, 600); // Match CSS animation duration

    // Handle WINNING attempt
    if (isWinningAttempt) {
      console.log('🎉 LEVEL COMPLETED!');

      setIsAnimatingSubmission(true);
      setShowConfetti(true);

      // Capture the level number to keep popup title stable
      setCompletedLevelNumber(currentLevel);

      // Immediately show level complete overlay (skip alerts/intermediate screens)
      setLevelCompleted(true);

      // Continue animations and persistence after brief delay
      setTimeout(async () => {


        // Save level completion to Firebase
        if (currentUser) {
          try {
            console.log(`💾 Saving level ${currentLevel} completion to Firebase...`);

            // Mark level as WON (new three-state system)
            await firebaseDatabase.setLevelStatus(
              currentUser.uid,
              currentLevel,
              'won',
              currentLanguage
            );
            console.log(`✅ Level ${currentLevel} marked as WON`);

            // Calculate next uncompleted level (start from currentLevel + 1 for forward progression)
            const nextLevel = await getNextUncompletedLevel(
              currentUser.uid,
              currentLanguage,
              currentLevel + 1  // Move forward, don't go back to earlier failed levels
            );
            console.log(`📊 Next uncompleted level: ${nextLevel}`);

            // Update current level state to advance to next level
            setCurrentLevel(nextLevel);
            console.log(`🔄 Advanced to level ${nextLevel}`);
          } catch (error) {
            console.error('❌ Failed to save level progress:', error);
            // Continue anyway - don't block the user
          }
        } else {
          console.log('📊 No user logged in, progress not saved');
        }

        setShowConfetti(false);
        setIsAnimatingSubmission(false);
      }, 1000);

      return;
    }

    // Handle FAILED attempt (valid word but not the target or insufficient score)
    const newAttemptsRemaining = attemptsRemaining - 1;
    setAttemptsRemaining(newAttemptsRemaining);

    console.log(`❌ Incorrect attempt. ${newAttemptsRemaining} attempts remaining.`);

    // Check if this was the last attempt
    if (newAttemptsRemaining === 0) {
      console.log('💔 LEVEL FAILED - No attempts remaining');

      // Show level failed popup (no alert)
      setLevelFailed(true);
      setSolutionRevealed(false); // Solution is hidden initially

      return;
    }

    // Still have attempts remaining - provide feedback and clear spelling bar
    const feedbackMessage = t('game.messages.incorrectWord', {
      word,
      score: currentScore,
      category: categoryHint,
      remaining: newAttemptsRemaining
    });

    // Show styled wrong word popup (replaces alert)
    setWrongWordMessage(feedbackMessage);
    setShowWrongWordPopup(true);

    // Return letters from spelling bar back to the letter pool
    const lettersToReturn = spellingBar.filter(letter => letter !== null);

    if (lettersToReturn.length > 0) {
      setLetters(prevLetters => {
        const newLetters = [...prevLetters];

        // Find empty slots in the letter pool and place returned letters
        lettersToReturn.forEach(letter => {
          // Reset the letter's inBar property
          const returnedLetter = { ...letter, inBar: false };

          // Find first empty slot
          const emptyIndex = newLetters.findIndex(l => l === null);
          if (emptyIndex !== -1) {
            newLetters[emptyIndex] = returnedLetter;
          } else {
            // If no empty slots, add to the end (shouldn't happen with 18 slots)
            console.warn('No empty slot found in letter pool, adding to end');
            newLetters.push(returnedLetter);
          }
        });

        return newLetters;
      });
    }

    // Clear spelling bar
    setSpellingBar(Array(10).fill(null));

    // Reset bought letters counter for next attempt
    setLettersBoughtThisRound(0);
  };

  return (
    <div
      className="game-container"
      onDrop={handleGlobalDrop}
      onDragOver={(e) => e.preventDefault()}
      onMouseMove={handleMouseMove}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className="game-content">
        <div className="game-header">
          {/* Level-Based Game HUD */}
          <div className="player-hud level-hud">
            {/* Language Toggle and User Controls Row */}
            <div className="hud-row controls-row">
              <LanguageToggle />
              {isAuthenticated ? (
                <button
                  className="user-button hud-user-button"
                  onClick={onShowUserProfile}
                >
                  👤 {playerData?.playername || currentUser?.displayName || t('app.player')}
                </button>
              ) : (
                <button
                  className="login-button hud-login-button"
                  onClick={onShowAuthModal}
                >
                  {t('app.signIn')}
                </button>
              )}
            </div>

            {/* Level Information Row */}
            <div className="hud-row level-info">
              <div className="hud-stat level-number">
                <span className="stat-label">{t('game.level')}</span>
                <span className="stat-value">{currentLevel}/100</span>
              </div>
              <div className="hud-stat attempts-remaining">
                <span className="stat-label">{t('game.attempts')}</span>
                <span className="stat-value attempts-value">{attemptsUsed.length}/5</span>
              </div>
            </div>
          </div>



          {/* Settings button - commented out for now but kept for future use */}
          {/* <button
            className="settings-btn"
            onClick={() => setShowSettings(true)}
            title="Letter Settings"
          >
            ⚙️
          </button> */}
        </div>

        {/* Game Controls - spelling bar, letter pool */}
        <div className="game-controls">
          {/* Level Completed Overlay */}
          {levelCompleted && (
            <div className="game-over-overlay level-complete-overlay">
              <div className="game-over-message level-complete-message">
                <h2>{t('game.levelComplete.title', { level: completedLevelNumber || currentLevel })}</h2>

                <div className="level-complete-stats">
                  <div className="stat-item">
                    {t('game.levelComplete.targetWord')} <strong>{targetWord}</strong>
                  </div>
                  <div className="stat-item">
                    {t('game.levelComplete.attemptsUsed')} <strong>{attemptsUsed.length}/3</strong>
                  </div>
                </div>

                <div className="level-complete-actions">
                  <button
                    className="next-level-btn"
                    onClick={async () => {
                      // Advance to next uncompleted level (forward progression)
                      if (currentUser) {
                        try {
                          const nextLevel = await getNextUncompletedLevel(
                            currentUser.uid,
                            currentLanguage,
                            currentLevel + 1  // Move forward, don't go back
                          );
                          console.log(`➡️ Advancing to next uncompleted level: ${nextLevel}`);

                          setCurrentLevel(nextLevel);
                        } catch (error) {
                          console.error('❌ Failed to get next level:', error);
                          // Fallback to simple increment
                          setCurrentLevel(prev => Math.min(prev + 1, 100));
                        }
                      } else {
                        // Not logged in - just increment
                        setCurrentLevel(prev => Math.min(prev + 1, 100));
                      }
                    }}
                  >
                    {t('game.levelComplete.nextLevel')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Message Popup for errors and invalid words */}
          <MessagePopup
            isOpen={showWrongWordPopup && !levelCompleted && !levelFailed}
            onClose={() => setShowWrongWordPopup(false)}
            title={t('game.wrongWord.title')}
            message={wrongWordMessage}
            type="error"
            dismissText={t('game.wrongWord.dismiss')}
          />

          {/* Level Failed Overlay */}
          {levelFailed && (
            <div className="game-over-overlay level-failed-overlay">
              <div className="game-over-message level-failed-message">
                <h2>{t('game.levelFailed.title', { level: currentLevel })}</h2>

                <div className="level-failed-stats">
                  {/* Only show solution after "See Solution" is clicked */}
                  {solutionRevealed && (
                    <div className="stat-item solution-revealed">
                      {t('game.levelFailed.targetWord')} <strong className="target-word-highlight">{targetWord}</strong>
                    </div>
                  )}
                  <div className="stat-item">
                    {t('game.levelFailed.category')} <strong>{categoryHint}</strong>
                  </div>
                  <div className="stat-item">
                    {t('game.levelFailed.attemptsUsed')} <strong>3/3</strong>
                  </div>
                </div>

                <div className="level-failed-actions">
                  {!solutionRevealed ? (
                    <>
                      {/* Before solution is revealed: Show Retry and See Solution buttons */}
                      <button
                        className="retry-level-btn"
                        onClick={() => {
                          // Reset the current level completely
                          setLevelFailed(false);
                          setSolutionRevealed(false);
                          setAttemptsRemaining(3);
                          setAttemptsUsed([]);
                          setHintsUsed(0);
                          setRevealedHintLetters([]);

                          // Clear spelling bar and return letters to pool
                          const lettersToReturn = spellingBar.filter(letter => letter !== null);
                          setLetters(prev => {
                            const newLetters = [...prev];
                            lettersToReturn.forEach(letter => {
                              const emptyIndex = newLetters.findIndex(l => l === null);
                              if (emptyIndex !== -1) {
                                newLetters[emptyIndex] = letter;
                              }
                            });
                            return newLetters;
                          });
                          setSpellingBar(Array(10).fill(null));

                          // Shuffle the letter pool for variety
                          setLetters(prev => {
                            const shuffled = [...prev];
                            for (let i = shuffled.length - 1; i > 0; i--) {
                              const j = Math.floor(Math.random() * (i + 1));
                              [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
                            }
                            return shuffled;
                          });
                        }}
                      >
                        🔄 {t('game.levelFailed.retryLevel')}
                      </button>
                      <button
                        className="see-solution-btn"
                        onClick={() => {
                          // Reveal the solution within the popup
                          setSolutionRevealed(true);
                        }}
                      >
                        💡 {t('game.levelFailed.seeSolution')}
                      </button>
                    </>
                  ) : (
                    <>
                      {/* After solution is revealed: Show Next Level button */}
                      <button
                        className="next-level-btn"
                        onClick={async () => {
                          console.log('🔴 Next Level clicked after failed level');

                          // Mark this level as FAILED in Firebase
                          if (currentUser) {
                            try {
                              console.log(`💾 Marking level ${currentLevel} as FAILED...`);
                              await firebaseDatabase.setLevelStatus(
                                currentUser.uid,
                                currentLevel,
                                'failed',
                                currentLanguage
                              );
                              console.log(`✅ Level ${currentLevel} marked as FAILED`);

                              // Get next uncompleted level (start searching from currentLevel + 1)
                              const nextLevel = await getNextUncompletedLevel(
                                currentUser.uid,
                                currentLanguage,
                                currentLevel + 1  // Skip the current failed level
                              );
                              console.log(`➡️ Advancing to next uncompleted level: ${nextLevel}`);

                              // Close popup and advance to next level
                              setLevelFailed(false);
                              setSolutionRevealed(false);
                              setCurrentLevel(nextLevel);
                            } catch (error) {
                              console.error('❌ Failed to save failed level status:', error);
                              // Fallback: close popup and increment level
                              setLevelFailed(false);
                              setSolutionRevealed(false);
                              setCurrentLevel(prev => Math.min(prev + 1, 100));
                            }
                          } else {
                            // Not logged in - just close popup and increment
                            console.log('📊 No user logged in, progress not saved');
                            setLevelFailed(false);
                            setSolutionRevealed(false);
                            setCurrentLevel(prev => Math.min(prev + 1, 100));
                          }
                        }}
                      >
                        {t('game.levelComplete.nextLevel')}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Game Over Overlay (DEPRECATED - for endless mode) */}
          {isGameOver && !levelCompleted && !levelFailed && (
              <div className="game-over-overlay">
                <div className="game-over-message">
                  <h2>Game Over!</h2>

                  {/* Combined Game Stats */}
                  {(() => {
                    const sessionStats = getSessionStats();
                    return (
                      <div className="game-over-stats compact">
                        {/* New Records First (if any) - excluding top words notification */}
                        {sessionStats.isNewBestGameScore && (
                          <div className="stat-item new-record">
                            🏆 New Best Game Score!
                          </div>
                        )}

                        {/* Compact Core Stats - inline format */}
                        <div className="stat-item compact-stat">
                          Final Score: {sessionStats.totalPoints}
                        </div>
                        <div className="stat-item compact-stat">
                          Words Submitted: {sessionStats.wordsSubmitted}
                        </div>
                        {sessionStats.bestWord && (
                          <div className="stat-item compact-stat">
                            Best Word: {sessionStats.bestWord.word} ({sessionStats.bestWord.points}pts)
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Registration prompt for anonymous users */}
                  {!isAuthenticated && (
                    <div className="registration-prompt">
                      <p>🏆 Want to save your progress and compete with others?</p>
                      <p className="save-progress-note">
                        Your session stats will be lost unless you sign in!
                      </p>
                      <button
                        className="register-btn"
                        onClick={onShowAuthModal}
                      >
                        Sign Up / Sign In
                      </button>
                    </div>
                  )}

                  {/* Player records for authenticated users */}
                  {isAuthenticated && playerData && (
                    <div className="player-achievements">
                      <h3>Your Records</h3>
                      <div className="achievement-item">
                        <span className="achievement-label">Total Score:</span>
                        <span className="achievement-value">{playerData.totalScore || 0}</span>
                      </div>
                      <div className="achievement-item">
                        <span className="achievement-label">Best Game Score:</span>
                        <span className="achievement-value">{playerData.bestGameScore || 0}</span>
                      </div>
                      {playerData.topWords && playerData.topWords.length > 0 && (
                        <div className="achievement-item">
                          <span className="achievement-label">Best Word:</span>
                          <span className="achievement-value">
                            {playerData.topWords[0].word} ({playerData.topWords[0].points}pts)
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                    <button
                      className="restart-btn"
                      onClick={() => {
                        resetGameState();
                        // Regenerate letters for new game
                        const newLetters = Array(18).fill(null);
                        const randomLetters = LetterGenerator.generateRandomLetters(settingsManager);
                        for (let i = 0; i < 15; i++) {
                          newLetters[i] = randomLetters[i];
                        }
                        setLetters(newLetters);
                        setSpellingBar(Array(10).fill(null));
                        generateSpellingBarModifiers();
                      }}
                    >
                      Start New Game
                    </button>
                  </div>
                </div>
              </div>
          )}

          {/* Attempt History Display */}
          {attemptsUsed.length > 0 && (
            <div className="attempt-history">
              <h3 className="attempt-history-title">Attempt History:</h3>
              <div className="attempt-list">
                {attemptsUsed.map((attempt, index) => (
                  <div key={index} className={`attempt-item ${attempt.isCorrect ? 'correct' : 'incorrect'}`}>
                    {/* Letter Feedback Row - Show which letters are in the target word */}
                    {!attempt.isCorrect && attempt.letterFeedback && attempt.letterFeedback.length > 0 && (
                      <div className="letter-feedback-row">
                        <span className="feedback-label">{t('game.letterFeedback.label')}</span>
                        <div className="letter-feedback-tiles">
                          {attempt.letterFeedback.map((feedback, letterIndex) => (
                            <span
                              key={letterIndex}
                              className={`feedback-letter ${feedback.status}`}
                              title={feedback.status === 'correct' ? t('game.letterFeedback.correctTooltip') : t('game.letterFeedback.absentTooltip')}
                            >
                              {feedback.letter}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Flying Word Animation */}
          {flyingWord && (
            <div className={`flying-word ${flyingWord.isCorrect ? 'correct' : 'incorrect'}`}>
              <span className="flying-word-text">{flyingWord.word}</span>
              <span className="flying-word-score">({flyingWord.score} pts)</span>
              <span className="flying-word-indicator">{flyingWord.isCorrect ? '✅' : '❌'}</span>
            </div>
          )}

          {/* Category Hint - Compact Display Above Spelling Bar */}
          <div className="category-hint-container">
            <div className="category-hint-box">
              <span className="category-label">💡 {t('game.category')}:</span>
              <span className="category-value">{categoryHint || 'Loading...'}</span>
            </div>
          </div>

          <div className={`spelling-bar-container ${exitingLetters.length > 0 ? 'fading-out' : ''}`}>
            <div className="spelling-bar">
              {spellingBar.map((letter, index) => {
                // Check if this slot is part of a word being dragged
                const isPartOfDraggedWord = isDraggingWord && draggedWord &&
                  index >= draggedWord.startIndex && index <= draggedWord.endIndex;

                // Check if this is a single letter being dragged
                const isSingleLetterDragging = isDraggingFromBar && !isDraggingWord && draggedIndex === index;

                // Get modifier for this position
                const modifier = spellingBarModifiers[index];

                let slotClass = 'spelling-slot';
                if (dragOverSlot === index) slotClass += ' drag-over';
                if (isPartOfDraggedWord) slotClass += ' word-dragging';
                if (isSingleLetterDragging) slotClass += ' single-letter-dragging';
                if (modifier) slotClass += ` modifier-${modifier.toLowerCase()}`;

                return (
                  <div
                    key={index}
                    className={slotClass}
                    onDragOver={(e) => handleSlotDragOver(e, index)}
                    onDragLeave={handleSlotDragLeave}
                    onDrop={(e) => handleDrop(e, index)}
                    style={{ position: 'relative' }}
                  >
                    {/* Modifier indicator (DL, TL, DW, TW - currently disabled for Phase 1) */}
                    {modifier && (
                      <div className="position-indicator modifier">{modifier}</div>
                    )}

                    {letter && (
                      <>
                        <LetterTile
                          letter={letter.char}
                          className="spelling-letter"
                          draggable={false}
                          onDoubleClick={() => handleReturnToRack(index)}
                          isHintRevealed={letter.isHintRevealed || false}
                        />
                        {/* Invisible drag handle that remains active */}
                        <div
                          className="drag-handle"
                          draggable={true}
                          onDragStart={(e) => handleDragStart(e, index, true)}
                          onDrag={handleDrag}
                          onDragEnd={handleDragEnd}
                          onTouchStart={(e) => handleTouchStart(e, index, true)}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            cursor: 'grab',
                            zIndex: 10,
                            backgroundColor: 'transparent'
                          }}
                        />
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <LetterPool
            letters={letters}
            dragOverPool={dragOverPool}
            isDraggingFromPool={isDraggingFromPool}
            draggedIndex={draggedIndex}
            enteringLetters={enteringLetters}
            fadingOut={exitingLetters.length > 0}
            onDragStart={handleDragStart}
            onDrag={handleDrag}
            onDragEnd={handleDragEnd}
            onTouchStart={handleTouchStart}
            onLetterTap={handleLetterTap}
            onPoolDragOver={handlePoolDragOver}
            onPoolDragLeave={handlePoolDragLeave}
            onDropOnPool={handleDropOnPool}
          />

        </div>

        {/* Button Section - Hint and Submit Word */}
        <div className="button-section">
          {/* Hint Button - Reveals a letter from the target word */}
          <button
            className={`hint-btn ${canUseHint() ? 'enabled' : 'disabled'}`}
            onClick={handleHint}
            disabled={!canUseHint()}
            title={
              hintsUsed >= 3
                ? t('game.noHintsRemaining')
                : levelCompleted || levelFailed
                  ? t('game.levelCompleted')
                  : !targetWord
                    ? t('game.noTargetWord')
                    : t('game.hintsRemaining', { remaining: 3 - hintsUsed })
            }
          >
            {t('game.hint')} ({hintsUsed}/3)
          </button>

          <button
            className={`submit-btn ${canSubmit && !isSubmitting ? 'enabled' : 'disabled'} ${isSubmitting ? 'submitting' : ''}`}
            onClick={handleWordSubmission}
            disabled={!canSubmit || isSubmitting}
            title={
              isSubmitting
                ? 'Validating word...'
                : canSubmit
                  ? t('game.submitEnabled')
                  : t('game.submitDisabled')
            }
          >
            {isSubmitting ? (
              <>
                <span className="spinner"></span>
                {t('game.validating', 'Validating...')}
              </>
            ) : (
              t('game.submitWord')
            )}
          </button>
        </div>
      </div>

      {showSettings && (
        <LetterSettings onClose={handleSettingsClose} />
      )}

      {/* Confetti Animation */}
      {showConfetti && (
        <div className="confetti-container">
          {Array.from({ length: 50 }, (_, i) => (
            <div
              key={i}
              className="confetti-piece"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 0.5}s`,
                backgroundColor: ['#FFD700', '#FF6B6B', '#4CAF50', '#2196F3', '#FF9800'][Math.floor(Math.random() * 5)]
              }}
            />
          ))}
        </div>
      )}



      {/* Floating word that follows cursor during drag */}
      {(isDraggingWord || (isTouchDragging && draggedWord)) && draggedWord && (
        <div
          className="floating-word"
          style={{
            left: (isTouchDragging ? touchCurrentPosition.x : cursorPosition.x) - 20,
            top: (isTouchDragging ? touchCurrentPosition.y : cursorPosition.y) - 30,
          }}
        >
          {draggedWord.letters.map((wordLetter, index) => (
            <div key={index} className="floating-letter-slot">
              <LetterTile
                letter={wordLetter.letter.char}
                className="floating-letter"
                draggable={false}
              />
            </div>
          ))}
        </div>
      )}

      {/* Floating single letter that follows cursor during drag */}
      {((isDraggingFromBar && !isDraggingWord) || (isTouchDragging && !draggedWord)) && draggedLetter && (
        <div
          className="floating-single-letter"
          style={{
            left: (isTouchDragging ? touchCurrentPosition.x : cursorPosition.x) - 20,
            top: (isTouchDragging ? touchCurrentPosition.y : cursorPosition.y) - 25,
          }}
        >
          <div className="floating-letter-slot">
            <LetterTile
              letter={draggedLetter.char}
              className="floating-letter"
              draggable={false}
            />
          </div>
        </div>
      )}
    </div>
  );
}
